import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import toastifyFile from "../../components/external-libraries/React-toastify/index";
import errorConstants from "../../constants/ErrorConstants";
import axoisConfigUtil from "../../utils/AxoisConfigUtil";
import AuthHeader from "./AuthHeader";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/input";
import { BsCheck2Circle } from "react-icons/bs";
import { BiError } from "react-icons/bi";
import { Stack } from "@chakra-ui/layout";
import languageConstants from "../../constants/LanguageConstants";
import { useEffect } from "react";

function NewPassword({ language, setLanguage }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t("title.projectName")} | ${t("title.newPass")}`;
  }, []);

  let navigate = useNavigate();

  function backTologin() {
    navigate(`/login`);
  }

  function submitNewPassword() {
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/reset-password`,
        {
          email: searchParams.get("email"),
          password: formik.values.password,
          password_confirmation: formik.values.passwordConfirmation,
          token: searchParams.get("token"),
        },
        axoisConfigUtil.getHeadersWithLang()
      )
      .then(function (response) {
        toastifyFile.successNotify(response.data.message);
        backTologin();
      })
      .catch(function (error) {
        document.getElementById("PasswordId").value = "";
        document.getElementById("passwordConfirmationId").value = "";
        if (!error.response) {
          // network error
          toastifyFile.errorNotify(
            t("general.messages.error.connectionRefused") +
              " : " +
              errorConstants.newPassword.code
          );
        }

        if (error.response.status === 401 || error.response.status === 400) {
          toastifyFile.errorNotify(error.response.data.message);
        } else {
          toastifyFile.errorNotify(
            t("general.messages.error.generalError") +
              " : " +
              errorConstants.newPassword.code
          );
        }
      });
  }

  const formik = useFormik({
    validateOnChange: true,
    initialValues: {
      password: "",
      passwordConfirmation: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required(t("formik.required"))
        .min(8, t("formik.minPassword")),
      passwordConfirmation: Yup.string()
        .required(t("formik.required"))
        .oneOf([Yup.ref("password"), null], t("formik.passwordConfirmation")),
    }),
    onSubmit: (values) => {
      submitNewPassword();
    },
  });

  return (
    <>
      <div className='min-h-screen flex flex-col gap-8 items-center bg-[#ebf7fd] custom-bg text-gray-500'>
        <AuthHeader
          pageType='newPass'
          language={language}
          setLanguage={setLanguage}
        />
        <div className='md:w-1/2 w-3/4 my-auto'>
          <form onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-2 p-7 bg-white rounded-tr-2xl rounded-bl-2xl'>
              <Stack spacing={4}>
                <label className='font-light text-xl' htmlFor='password'>
                  {t("signUp.password")}
                </label>
                <InputGroup>
                  <Input
                    id='passwordId'
                    name='password'
                    type='password'
                    required
                    isInvalid={
                      formik.errors.password && formik.touched.password
                    }
                    errorBorderColor='red.300'
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                  />
                  {language === languageConstants.AR ? (
                    <InputLeftElement
                      children={
                        !formik.errors.password && formik.touched.password ? (
                          <BsCheck2Circle className='text-green-700' />
                        ) : formik.errors.password &&
                          formik.touched.password ? (
                          <BiError className='text-red-400' />
                        ) : (
                          <></>
                        )
                      }
                    />
                  ) : (
                    <InputRightElement
                      children={
                        !formik.errors.password && formik.touched.password ? (
                          <BsCheck2Circle className='text-green-700' />
                        ) : formik.errors.password &&
                          formik.touched.password ? (
                          <BiError className='text-red-400' />
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                </InputGroup>
                {formik.errors.password && formik.touched.password && (
                  <div className='text-red-400'>* {formik.errors.password}</div>
                )}

                <label
                  className='font-light text-xl'
                  htmlFor='passwordConfirmation'
                >
                  {t("signUp.passwordConfirmation")}
                </label>
                <InputGroup>
                  <Input
                    id='passwordConfirmationId'
                    name='passwordConfirmation'
                    type='password'
                    required
                    isInvalid={
                      formik.errors.passwordConfirmation &&
                      formik.touched.passwordConfirmation
                    }
                    errorBorderColor='red.300'
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                  />
                  {language === languageConstants.AR ? (
                    <InputLeftElement
                      children={
                        !formik.errors.passwordConfirmation &&
                        formik.touched.passwordConfirmation ? (
                          <BsCheck2Circle className='text-green-700' />
                        ) : formik.errors.passwordConfirmation &&
                          formik.touched.passwordConfirmation ? (
                          <BiError className='text-red-400' />
                        ) : (
                          <></>
                        )
                      }
                    />
                  ) : (
                    <InputRightElement
                      children={
                        !formik.errors.passwordConfirmation &&
                        formik.touched.passwordConfirmation ? (
                          <BsCheck2Circle className='text-green-700' />
                        ) : formik.errors.passwordConfirmation &&
                          formik.touched.passwordConfirmation ? (
                          <BiError className='text-red-400' />
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                </InputGroup>
                {formik.errors.passwordConfirmation &&
                  formik.touched.passwordConfirmation && (
                    <div className='text-red-400'>
                      * {formik.errors.passwordConfirmation}
                    </div>
                  )}
              </Stack>
            </div>
            <button
              type='submit'
              className='w-1/3 hover:w-[37%] transition-all mx-auto mt-8 block py-2 text-lg font-light rounded-full text-white bg-primary hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
            >
              {t("newPassword.save")}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default NewPassword;
